import type { PropsWithChildren } from 'react';
import React from 'react';

import { logError } from 'lib/ddlogHelper';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';

interface Props extends PropsWithChildren {
  accountId?: string;
  projectId?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage?: string;
  errorInfo?: string;
}

class ErrorBoundary extends React.Component<Props, ErrorBoundaryState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: undefined,
      errorInfo: undefined,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      hasError: true,
      errorMessage: error.message,
      errorInfo: errorInfo.componentStack ?? undefined,
    });

    logError(
      error,
      {
        source: 'error_boundary',
        componentStack: errorInfo.componentStack,
      },
      this.props.accountId,
      this.props.projectId,
    );
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage type={ErrorStatus.UNHANDLED_ERROR} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
